import { Injectable } from '@angular/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { PDFDocument } from 'pdf-lib';
import { ITicketsPDF } from '../api/legacy/tickets-service/tickets-model';
import { State } from '../store/state.service';

@Injectable()
export class PdfService {

  constructor(
    private state: State
  ) { }

  public generateTickets(attendeeTickets: ITicketsPDF, ticket: string, isPrint: boolean, eventName: string) {
    // Set loading
    this.state.setLoading(true);

    const ticketData = [];
    const ticketUrls = [];
    const pdfName = 'Tickets' + '_' + eventName;

    // If ticket is single, add it to the array of tickets
    if (ticket) {
      ticketData.push(ticket);
    // If download all, go through all attendee tickets
    } else {
      attendeeTickets?.tickets?.forEach(attendeeTicket => {
        if (attendeeTicket?.isGenerated) {
          ticketData.push(attendeeTicket.content);
        } else {
          ticketUrls.push(attendeeTicket.content);
        }
      });
    }

    // Generate PDF document
    const pdf = new jsPDF('p', 'mm', 'a4', true);

    // Create HTML element used for generating ticket pages
    const iframe = document.createElement('iframe');
    iframe.style.width = '0';
    iframe.style.height = '0';
    document.body.appendChild(iframe);
    const iframedoc = iframe.contentDocument;

    // Check if there is PDF data to generate
    if (ticketData?.length > 0) {
      this.generatePdf(pdf, iframedoc, 0, iframe, isPrint, ticketData, ticketUrls, pdfName);
    } else {
      this.finalizePdf(pdf, iframe, isPrint, ticketData, ticketUrls, pdfName);
    }
  }

  private generatePdf(pdf: jsPDF, iframedoc: Document, index: number, iframe: HTMLIFrameElement, isPrint: boolean, ticketData: string[], ticketUrls: string[], pdfName: string) {
    // Set document body to ticket page HTML
    iframedoc.body.innerHTML = ticketData[index];

    // Generate canvas from HTML
    html2canvas(iframedoc.body, { useCORS: true }).then(canvas => {
      const contentDataURL = canvas.toDataURL('image/png');
      const width = pdf.internal.pageSize.getWidth();
      const height = canvas.height * width / canvas.width;
      pdf.addImage(contentDataURL, 'PNG', 0, 0, width, height, '', 'FAST');

      // Clear canvas to avoid memory issues
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // If there are more ticket pages, add a new PDF page and recursively call the generate PDF method
      if (index < ticketData?.length-1) {
        pdf.addPage();
        this.generatePdf(pdf, iframedoc, index+1, iframe, isPrint, ticketData, ticketUrls, pdfName);
      // If there are no more pages, process the PDF for merging, printing or saving
      } else {
        this.finalizePdf(pdf, iframe, isPrint, ticketData, ticketUrls, pdfName);
      }
    });
  }

  private async finalizePdf(pdf: jsPDF, iframe: HTMLIFrameElement, isPrint: boolean, ticketData: string[], ticketUrls: string[], pdfName: string) {
    // Check if there are uploaded pdf tickets that need to be merged with the generated pdf
    if (ticketUrls?.length > 0) {
      const pdfDoc = await PDFDocument.create();

      // Generated PDF
      if (ticketData?.length > 0) {
        const generatedBytes = pdf.output('arraybuffer');
        const generatedDoc = await PDFDocument.load(generatedBytes);
        const generatedPage = await pdfDoc.copyPages(generatedDoc, generatedDoc.getPageIndices());
        generatedPage.forEach((page) => pdfDoc.addPage(page));
      }

      // Uploaded PDFs
      for await (const ticketUrl of ticketUrls) {
        const uploadedBytes = await fetch(ticketUrl).then(res => res.arrayBuffer());
        const uploadedDoc = await PDFDocument.load(uploadedBytes);
        const uploadedPage = await pdfDoc.copyPages(uploadedDoc, uploadedDoc.getPageIndices());
        uploadedPage.forEach((page) => pdfDoc.addPage(page));
      }

      const pdfBytes = await pdfDoc.save();
      const file = new Blob([pdfBytes], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    } else {
      if (isPrint) {
        pdf.autoPrint();
        window.open(pdf.output('bloburl').toString(), '_blank');
      } else {
        pdf.save(pdfName + '.pdf');
      }
    }

    // Set loading
    this.state.setLoading(false);

    // Remove the HTML element for generating the PDF
    document.body.removeChild(iframe);
  }

    async downloadPDFMultiple(isPrint: boolean, htmlContents: string[], height: number, width: number, name: string): Promise<void> {
        this.state.setLoading(true);
        const PDF = new jsPDF({
            orientation: (width <= height) ? 'portrait' : 'landscape',
            unit: 'mm',
            format: [width, height]
        });

        for (let i = 0; i < htmlContents.length; i++) {
            const DATA = document.createElement('div');
            DATA.innerHTML = htmlContents[i];
            DATA.style.width = width + 'mm';
            DATA.style.height = height + 'mm';
            DATA.style.overflow = 'hidden';
            DATA.style.fontSize = '10px';
            DATA.style.lineHeight = 'normal';
            document.body.appendChild(DATA);

            try {
                const canvas = await html2canvas(DATA, {
                    windowWidth: width,
                    windowHeight: height,
                    useCORS: true,
                    scale: 3
                });

                if (i > 0) {
                    PDF.addPage([width, height]);
                }

                const imgData = canvas.toDataURL('image/png');

                const contentAspectRatio = canvas.width / canvas.height;
                const desiredAspectRatio = width / height;

                let imgWidth = width;
                let imgHeight = height;

                if (contentAspectRatio > desiredAspectRatio) {
                    imgHeight = width / contentAspectRatio;
                } else {
                    imgWidth = height * contentAspectRatio;
                }

                PDF.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, '', 'FAST');

            } finally {
                document.body.removeChild(DATA);
            }
        }

        const pdfBlob = PDF.output('blob');
        const blobURL = URL.createObjectURL(pdfBlob);

        if (isPrint) {
            PDF.autoPrint();
            window.open(PDF.output('bloburl').toString(), '_blank');
        } else {
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = blobURL;
            a.target = '_blank';

            document.body.appendChild(a);
            a.click();

            document.body.removeChild(a);
        }
        this.state.setLoading(false);
    }
}
